import type { ComponentPropsWithRef, ComponentType, SVGProps } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '../icon/icon';

// Icon slug brush-stroke backgrounds — one SVG per type (scales to any size via CSS)
import slug1Url from './svgs/slug-1.svg?url';
import slug2Url from './svgs/slug-2.svg?url';
import slug3Url from './svgs/slug-3.svg?url';
// Copy: 32px tall brush strokes — text overlaid on top, one per type
import slugCopy1Url from './svgs/slug-copy-1.svg?url';
import slugCopy2Url from './svgs/slug-copy-2.svg?url';
import slugCopy3Url from './svgs/slug-copy-3.svg?url';

// Per-type slug background URLs — SVGs scale freely to CSS-controlled container dimensions
const BG_URL: Record<'1' | '2' | '3', string> = {
    '1': slug1Url,
    '2': slug2Url,
    '3': slug3Url,
};

// Slug background dimensions and margin-top per size
const BG_CLASS: Record<'lg' | 'md' | 'sm', string> = {
    lg: 'h-6 w-20 mt-1', // 24px × 80px, mt 4px
    md: 'h-[18px] w-14 mt-[3px]', // 18px × 56px, mt 3px
    sm: 'h-3 w-10 mt-0.5', // 12px × 40px, mt 2px
};

// Icon container size per size
const ICON_CLASS: Record<'lg' | 'md' | 'sm', string> = {
    lg: 'size-8', // 32px
    md: 'size-6', // 24px
    sm: 'size-4', // 16px
};

// Icon horizontal offset within the CSS grid cell per size and type
// (icon overlaps the slug background, positioned from the left edge of the grid cell)
const ICON_ML: Record<'lg' | 'md' | 'sm', Record<'1' | '2' | '3', string>> = {
    lg: { '1': 'ml-7', '2': 'ml-6', '3': 'ml-6' }, // 28px / 24px / 24px
    md: { '1': 'ml-[18px]', '2': 'ml-4', '3': 'ml-[18px]' }, // type 2 uses 16px per Figma
    sm: { '1': 'ml-3.5', '2': 'ml-3.5', '3': 'ml-3.5' }, // 14px
};

// Gap between the slug+icon group and the label text per size
const GAP_CLASS: Record<'lg' | 'md' | 'sm', string> = {
    lg: 'gap-2', // 8px
    md: 'gap-2', // 8px
    sm: 'gap-1', // 4px
};

// Label text style per size
const TEXT_CLASS: Record<'lg' | 'md' | 'sm', string> = {
    lg: 'text-base leading-5 tracking-[0.01em]', // 16px / line-height 20 / tracking 0.16px
    md: 'text-sm leading-[18px]', // 14px
    sm: 'text-sm leading-[18px]', // 14px
};

// Copy slug: brush-stroke SVG with text overlaid on top (no icon)
const COPY_BG_URL: Record<'1' | '2' | '3', string> = {
    '1': slugCopy1Url,
    '2': slugCopy2Url,
    '3': slugCopy3Url,
};

export interface SlugProps extends ComponentPropsWithRef<'div'> {
    type?: '1' | '2' | '3';
    size?: 'lg' | 'md' | 'sm';
    icon?: ComponentType<SVGProps<SVGSVGElement>>;
    content?: 'copy';
}

export const Slug = ({ type = '1', size = 'lg', icon, content, className, ref, children, ...props }: SlugProps) => {
    if (content === 'copy') {
        return (
            <div
                data-slot="slug"
                ref={ref}
                className={cn('relative inline-flex h-8 shrink-0 items-center', className)}
                {...props}>
                <div data-slot="slug-bg" className="absolute inset-0">
                    <img alt="" className="block size-full max-w-none" src={COPY_BG_URL[type]} />
                </div>
                {children ? (
                    <span
                        data-slot="slug-label"
                        className="relative px-4 font-sans font-normal text-[18px] leading-[18px] tracking-[1.44px] uppercase whitespace-nowrap text-snk-black">
                        {children}
                    </span>
                ) : null}
            </div>
        );
    }

    const bgUrl = BG_URL[type];
    const bgClass = BG_CLASS[size];
    const iconClass = ICON_CLASS[size];
    const iconMl = ICON_ML[size][type];
    const gapClass = GAP_CLASS[size];
    const textClass = TEXT_CLASS[size];

    return (
        <div data-slot="slug" ref={ref} className={cn('inline-flex items-center', gapClass, className)} {...props}>
            {/* slug+icon: CSS grid so the brush-stroke background and icon share the same cell */}
            <div
                data-slot="slug-badge"
                className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start leading-none relative shrink-0">
                {/* Layer 1: brush-stroke SVG background */}
                <div data-slot="slug-bg" className={cn('col-[1] row-[1] relative ml-0', bgClass)}>
                    <img alt="" className="absolute inset-0 block size-full max-w-none" src={bgUrl} />
                </div>
                {/* Layer 2: icon — overlaps the background in the same grid cell */}
                {icon ? (
                    <div data-slot="slug-icon" className={cn('col-[1] row-[1] relative mt-0', iconClass, iconMl)}>
                        <Icon icon={icon} className="size-full" />
                    </div>
                ) : null}
            </div>
            {/* Label text */}
            {children ? (
                <span
                    data-slot="slug-label"
                    className={cn(
                        'relative shrink-0 whitespace-nowrap font-sans font-light text-snk-black',
                        textClass
                    )}>
                    {children}
                </span>
            ) : null}
        </div>
    );
};
